import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
    ArrowLeft, 
    Heart,
    X,
    ChevronRight,
    ChevronDown,
    ChevronUp,
    Sparkles,
    MessageCircle,
    Hand,
    Wind,
    Check,
    RefreshCw,
    Users
} from 'lucide-react';
import { IconButton, PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard, SelectionCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';

/**
 * "What Do We Need Right Now?" Repair Tool
 * 
 * A gentle, trauma-informed tool to help couples:
 * - Shift out of a negative relationship cycle
 * - Return to emotional safety and connection
 * - Communicate needs softly and without blame
 * 
 * Design principles:
 * - Soft, validating, non-blaming language
 * - User-led, optional flow at every step
 * - ADHD-friendly: calming, spacious, digestible
 * - Focused on the need, not the partner's behaviour
 */

const WhatDoWeNeed = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState('intro');
    // Steps: intro, notice, softer, need, share, partner, coregulate, reconnect
    
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [selectedSofter, setSelectedSofter] = useState(null);
    const [selectedNeed, setSelectedNeed] = useState(null);
    const [selectedShare, setSelectedShare] = useState(null);
    const [selectedResponse, setSelectedResponse] = useState(null);
    const [selectedGround, setSelectedGround] = useState(null);

    // Step 1: Notice what's happening (body states)
    const noticeOptions = [
        { id: 'tense', label: 'I feel tense or activated', icon: '⚡' },
        { id: 'shutdown', label: 'I feel shut down or distant', icon: '🌙' },
        { id: 'hurt', label: 'I feel hurt or scared', icon: '💔' },
        { id: 'unsure', label: "I'm not sure", icon: '🤷' }
    ];

    // Step 2: Softer feeling underneath
    const softerOptions = [
        { id: 'alone', label: 'I feel alone' },
        { id: 'losing', label: "I feel scared I'm losing you" },
        { id: 'overwhelmed', label: 'I feel overwhelmed' },
        { id: 'not-enough', label: 'I feel not good enough' },
        { id: 'misunderstood', label: 'I feel misunderstood' },
        { id: 'disconnected', label: 'I feel disconnected' },
        { id: 'unsure', label: "I'm not sure" }
    ];

    // Step 3: What you need right now
    const needOptions = [
        { id: 'reassurance', label: 'I need reassurance' },
        { id: 'breathe', label: 'I need a moment to breathe' },
        { id: 'heard', label: 'I need to feel heard' },
        { id: 'closeness', label: 'I need closeness' },
        { id: 'clarity', label: 'I need clarity' },
        { id: 'gentleness', label: 'I need gentleness' },
        { id: 'space', label: 'I need space, but not distance' },
        { id: 'unsure', label: "I'm not sure" }
    ];

    // Step 4: Sentence starters for sharing
    const shareStarters = [
        { id: 'feeling-need', label: "I'm feeling… and I need…" },
        { id: 'overwhelmed', label: "I'm overwhelmed and I need a moment." },
        { id: 'scared', label: "I'm scared and I need reassurance." },
        { id: 'shutdown', label: "I'm shut down and I need gentleness." },
        { id: 'hurting', label: "I'm hurting and I need to feel heard." }
    ];

    // Step 5: Partner response options
    const partnerResponses = [
        { id: 'here', label: "I'm here." },
        { id: 'thank-you', label: 'Thank you for telling me.' },
        { id: 'didnt-realise', label: "I didn't realise you felt that way." },
        { id: 'okay', label: "We're okay." },
        { id: 'understand', label: 'I want to understand.' },
        { id: 'slow-down', label: "Let's slow down together." }
    ];

    // Step 6: Co-regulation grounding options
    const groundingOptions = [
        { id: 'breath', label: 'Take one slow breath together.', icon: Wind },
        { id: 'heart', label: 'Place a hand on your heart or chest.', icon: Heart },
        { id: 'closer', label: 'Sit closer if it feels safe.', icon: Users },
        { id: 'touch', label: 'Hold hands or touch gently if you both want to.', icon: Hand }
    ];

    const handleClose = () => {
        navigate('/regulate/coregulation');
    };

    const handleBack = () => {
        const stepOrder = ['intro', 'notice', 'softer', 'need', 'share', 'partner', 'coregulate', 'reconnect'];
        const currentIndex = stepOrder.indexOf(step);
        if (currentIndex > 0) {
            setStep(stepOrder[currentIndex - 1]);
        } else {
            handleClose();
        }
    };

    const handleNext = () => {
        const stepOrder = ['intro', 'notice', 'softer', 'need', 'share', 'partner', 'coregulate', 'reconnect'];
        const currentIndex = stepOrder.indexOf(step);
        if (currentIndex < stepOrder.length - 1) {
            setStep(stepOrder[currentIndex + 1]);
        }
    };

    const handleSelectNotice = (option) => {
        setSelectedNotice(option);
    };

    const handleSelectSofter = (option) => {
        setSelectedSofter(option);
    };

    const handleSelectNeed = (option) => {
        setSelectedNeed(option);
    };

    const handleSelectShare = (option) => {
        setSelectedShare(option);
    };

    const handleSelectResponse = (option) => {
        setSelectedResponse(option);
    };

    const handleSelectGround = (option) => {
        setSelectedGround(option);
    };

    const handleStartOver = () => {
        setStep('intro');
        setSelectedNotice(null);
        setSelectedSofter(null);
        setSelectedNeed(null);
        setSelectedShare(null);
        setSelectedResponse(null);
        setSelectedGround(null);
    };

    return (
        <div className="min-h-screen bg-transparent">
            {/* Header */}
            <header className="sticky top-0 z-50 glass-header">
                <Container size="sm" className="py-5 flex items-center justify-between">
                    <IconButton 
                        onClick={handleBack}
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <Heading as="h1" size="text-lg">What Do We Need?</Heading>
                    <IconButton 
                        onClick={handleClose}
                        data-testid="close-btn"
                        className="hover:bg-[#FFBB98]/10"
                    >
                        <X className="w-5 h-5" />
                    </IconButton>
                </Container>
            </header>

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* INTRO */}
                {step === 'intro' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-10">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-4">
                                A gentle reset for both of you
                            </Heading>
                        </div>

                        <div className="glass-card rounded-2xl p-5">
                            <BodyText className="text-[#1D6B6B] leading-relaxed text-center text-lg">
                                When conflict feels fast, familiar, or overwhelming, it can help to pause and check in with what each person truly needs.
                            </BodyText>
                        </div>

                        <div className="p-5 rounded-2xl bg-[#7D8E95]/10 border border-[#7D8E95]/20 mb-8">
                            <BodyText className="text-[#2A8585] leading-relaxed text-center italic">
                                This tool guides both partners through a gentle reset so you can reconnect.
                            </BodyText>
                        </div>

                        <div className="space-y-4">
                            <PrimaryButton
                                onClick={() => setStep('notice')}
                                className="w-full"
                                size="xl"
                                data-testid="start-btn"
                            >
                                Start
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>

                        <div className="mt-8 text-center">
                            <SmallText className="text-[#7D8E95]">
                                You can pause or leave at any point. This is entirely optional.
                            </SmallText>
                        </div>
                    </div>
                )}

                {/* STEP 1: NOTICE WHAT'S HAPPENING */}
                {step === 'notice' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="blue" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Sparkles className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                Notice what's happening
                            </Heading>
                            <BodyText className="text-[#2A8585] leading-relaxed">
                                Take a moment to notice what's happening inside you. Is your body tense, shut down, buzzing, or overwhelmed?
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {noticeOptions.map((option, index) => {
                                const cardVariants = ['glass-card-sage', 'glass-card-lavender', 'glass-card-rose', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        className={`w-full text-left p-4 rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all brand-slide-up ${selectedNotice?.id === option.id ? 'ring-2 ring-[#1D6B6B]' : ''}`}
                                        style={{ animationDelay: `${index * 60}ms` }}
                                        onClick={() => handleSelectNotice(option)}
                                        data-testid={`notice-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <span className="text-xl">{option.icon}</span>
                                            <BodyText className="text-[#1D6B6B] flex-1">
                                                {option.label}
                                            </BodyText>
                                            {selectedNotice?.id === option.id && (
                                                <Check className="w-5 h-5 text-[#1D6B6B]" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="p-4 rounded-xl glass-card-gold rounded-xl mb-8">
                            <SmallText className="text-[#7D8E95] leading-relaxed text-center">
                                It's okay not to know. This is just about noticing.
                            </SmallText>
                        </div>

                        <div className="flex gap-4">
                            <SecondaryButton onClick={handleBack} className="flex-1" size="lg">
                                Back
                            </SecondaryButton>
                            <PrimaryButton 
                                onClick={handleNext} 
                                className="flex-1" 
                                size="lg"
                                disabled={!selectedNotice}
                            >
                                Continue
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {/* STEP 2: NAME THE SOFTER FEELING */}
                {step === 'softer' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                The softer feeling underneath
                            </Heading>
                            <BodyText className="text-[#2A8585] leading-relaxed">
                                Underneath frustration or withdrawal, there's often a softer feeling. Which of these feels closest?
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {softerOptions.map((option, index) => {
                                const cardVariants = ['glass-card-rose', 'glass-card-sage', 'glass-card-lavender', 'glass-card-gold', 'glass-card', 'glass-card-rose', 'glass-card-sage'];
                                return (
                                    <button
                                        key={option.id}
                                        className={`w-full text-left p-4 rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all brand-slide-up ${selectedSofter?.id === option.id ? 'ring-2 ring-[#FFBB98]' : ''}`}
                                        style={{ animationDelay: `${index * 50}ms` }}
                                        onClick={() => handleSelectSofter(option)}
                                        data-testid={`softer-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <BodyText className="text-[#1D6B6B] flex-1">
                                                {option.label}
                                            </BodyText>
                                            {selectedSofter?.id === option.id && (
                                                <Check className="w-5 h-5 text-[#FFBB98]" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="p-5 rounded-2xl bg-[#7D8E95]/10 border border-[#7D8E95]/20 mb-8">
                            <SmallText className="text-[#2A8585] leading-relaxed text-center">
                                These softer feelings are often what the cycle is protecting. Naming them helps slow everything down.
                            </SmallText>
                        </div>

                        <div className="flex gap-4">
                            <SecondaryButton onClick={handleBack} className="flex-1" size="lg">
                                Back
                            </SecondaryButton>
                            <PrimaryButton 
                                onClick={handleNext} 
                                className="flex-1" 
                                size="lg"
                                disabled={!selectedSofter}
                            >
                                Continue
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {/* STEP 3: IDENTIFY WHAT YOU NEED */}
                {step === 'need' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="sage" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Hand className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                What do you need right now?
                            </Heading>
                            <BodyText className="text-[#2A8585] leading-relaxed">
                                Choose what feels truest, even if it's just a guess.
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {needOptions.map((option, index) => {
                                const cardVariants = ['glass-card-sage', 'glass-card-rose', 'glass-card-lavender', 'glass-card-gold', 'glass-card', 'glass-card-sage', 'glass-card-rose', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        className={`w-full text-left p-4 rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all brand-slide-up ${selectedNeed?.id === option.id ? 'ring-2 ring-[#1D6B6B]' : ''}`}
                                        style={{ animationDelay: `${index * 50}ms` }}
                                        onClick={() => handleSelectNeed(option)}
                                        data-testid={`need-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <BodyText className="text-[#1D6B6B] flex-1">
                                                {option.label}
                                            </BodyText>
                                            {selectedNeed?.id === option.id && (
                                                <Check className="w-5 h-5 text-[#1D6B6B]" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="p-4 rounded-xl bg-[#7D8E95]/10 border border-[#7D8E95]/20 mb-8">
                            <SmallText className="text-[#2A8585] leading-relaxed text-center italic">
                                It's okay if you're unsure. Sometimes the need becomes clearer once the nervous system settles.
                            </SmallText>
                        </div>

                        <div className="flex gap-4">
                            <SecondaryButton onClick={handleBack} className="flex-1" size="lg">
                                Back
                            </SecondaryButton>
                            <PrimaryButton 
                                onClick={handleNext} 
                                className="flex-1" 
                                size="lg"
                                disabled={!selectedNeed}
                            >
                                Continue
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {/* STEP 4: SHARE IT IN A SOFT WAY */}
                {step === 'share' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="lavender" size="lg" useGradient={true} className="mx-auto mb-5">
                                <MessageCircle className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                Share it softly
                            </Heading>
                            <BodyText className="text-[#2A8585] leading-relaxed">
                                Try expressing your need gently. These sentence starters might help:
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {shareStarters.map((option, index) => {
                                const cardVariants = ['glass-card-lavender', 'glass-card-sage', 'glass-card-rose', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        className={`w-full text-left p-4 rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all brand-slide-up ${selectedShare?.id === option.id ? 'ring-2 ring-[#7D8E95]' : ''}`}
                                        style={{ animationDelay: `${index * 60}ms` }}
                                        onClick={() => handleSelectShare(option)}
                                        data-testid={`share-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <BodyText className="text-[#1D6B6B] flex-1 italic">
                                                "{option.label}"
                                            </BodyText>
                                            {selectedShare?.id === option.id && (
                                                <Check className="w-5 h-5 text-[#7D8E95]" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Dynamic prompt based on selections */}
                        {selectedSofter && selectedNeed && (
                            <div className="glass-card rounded-2xl p-5">
                                <SmallText className="text-[#7D8E95] font-medium mb-2 block text-center">
                                    Based on what you shared:
                                </SmallText>
                                <BodyText className="text-[#1D6B6B] text-center italic text-lg">
                                    "{selectedSofter.label.replace('I feel ', "I'm feeling ")} and {selectedNeed.label.toLowerCase()}."
                                </BodyText>
                            </div>
                        )}

                        <div className="p-4 rounded-xl glass-card-gold rounded-xl mb-8">
                            <SmallText className="text-[#7D8E95] leading-relaxed text-center">
                                Focus on the feeling and the need—not what your partner did.
                            </SmallText>
                        </div>

                        <div className="flex gap-4">
                            <SecondaryButton onClick={handleBack} className="flex-1" size="lg">
                                Back
                            </SecondaryButton>
                            <PrimaryButton onClick={handleNext} className="flex-1" size="lg">
                                Continue
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {/* STEP 5: PARTNER RESPONSE (OPTIONAL) */}
                {step === 'partner' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Users className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                Partner response
                            </Heading>
                            <BodyText className="text-[#2A8585] leading-relaxed">
                                If your partner is here, they might respond in a soothing way:
                            </BodyText>
                        </div>

                        <div className="p-4 rounded-xl bg-[#7D8E95]/10 border border-[#7D8E95]/20 mb-6">
                            <SmallText className="text-[#2A8585] leading-relaxed text-center">
                                This step is optional. You can skip it if you're doing this alone.
                            </SmallText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {partnerResponses.map((option, index) => {
                                const cardVariants = ['glass-card-rose', 'glass-card-sage', 'glass-card-lavender', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        className={`w-full text-left p-4 rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all brand-slide-up ${selectedResponse?.id === option.id ? 'ring-2 ring-[#FFBB98]' : ''}`}
                                        style={{ animationDelay: `${index * 60}ms` }}
                                        onClick={() => handleSelectResponse(option)}
                                        data-testid={`response-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <BodyText className="text-[#1D6B6B] flex-1 italic">
                                                "{option.label}"
                                            </BodyText>
                                            {selectedResponse?.id === option.id && (
                                                <Check className="w-5 h-5 text-[#FFBB98]" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="p-5 rounded-2xl bg-[#7D8E95]/10 border border-[#7D8E95]/20 mb-8">
                            <SmallText className="text-[#2A8585] leading-relaxed text-center italic">
                                Responding to the softer feeling helps shift the cycle toward connection.
                            </SmallText>
                        </div>

                        <div className="flex gap-4">
                            <SecondaryButton onClick={handleBack} className="flex-1" size="lg">
                                Back
                            </SecondaryButton>
                            <PrimaryButton onClick={handleNext} className="flex-1" size="lg">
                                Continue
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>

                        <div className="mt-4 text-center">
                            <TertiaryButton onClick={handleNext} data-testid="skip-partner">
                                Skip this step
                            </TertiaryButton>
                        </div>
                    </div>
                )}

                {/* STEP 6: CO-REGULATION MOMENT */}
                {step === 'coregulate' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="blue" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Wind className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                A moment together
                            </Heading>
                            <BodyText className="text-[#2A8585] leading-relaxed">
                                Choose a brief grounding moment for both of you:
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-8">
                            {groundingOptions.map((option, index) => {
                                const IconComponent = option.icon;
                                const cardVariants = ['glass-card-sage', 'glass-card-rose', 'glass-card-lavender'];
                                const iconGradients = [
                                    'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)',
                                    'linear-gradient(145deg, #FFBB98 0%, #E09060 100%)',
                                    'linear-gradient(145deg, #E8A0A0 0%, #C97B7B 100%)'
                                ];
                                return (
                                    <button
                                        key={option.id}
                                        className={`w-full text-left p-4 rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all brand-slide-up ${selectedGround?.id === option.id ? 'ring-2 ring-[#7D8E95]' : ''}`}
                                        style={{ animationDelay: `${index * 80}ms` }}
                                        onClick={() => handleSelectGround(option)}
                                        data-testid={`ground-${option.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div 
                                                className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md"
                                                style={{ background: iconGradients[index % iconGradients.length], color: 'white' }}
                                            >
                                                <IconComponent className="w-5 h-5" />
                                            </div>
                                            <BodyText className="text-[#1D6B6B] flex-1">
                                                {option.label}
                                            </BodyText>
                                            {selectedGround?.id === option.id && (
                                                <Check className="w-5 h-5 text-[#7D8E95]" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Breathing animation if breath is selected */}
                        {selectedGround?.id === 'breath' && (
                            <div className="flex justify-center mb-8">
                                <div 
                                    className="w-20 h-20 rounded-full bg-[#7D8E95]/40 flex items-center justify-center"
                                    style={{ 
                                        animation: 'breathe 4s ease-in-out infinite'
                                    }}
                                >
                                    <Wind className="w-8 h-8 text-[#7D8E95]" />
                                </div>
                            </div>
                        )}

                        <style>{`
                            @keyframes breathe {
                                0%, 100% { transform: scale(1); opacity: 0.7; }
                                50% { transform: scale(1.15); opacity: 1; }
                            }
                        `}</style>

                        <div className="flex gap-4">
                            <SecondaryButton onClick={handleBack} className="flex-1" size="lg">
                                Back
                            </SecondaryButton>
                            <PrimaryButton onClick={handleNext} className="flex-1" size="lg">
                                Continue
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {/* STEP 7: RECONNECT */}
                {step === 'reconnect' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Sparkles className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                Reconnect
                            </Heading>
                        </div>

                        <div className="glass-card rounded-2xl p-5">
                            <BodyText className="text-[#1D6B6B] leading-relaxed text-center text-lg">
                                When both people slow down, name what's happening inside, and reach for each other with softness, the cycle loses its power.
                            </BodyText>
                        </div>

                        <div className="p-5 rounded-2xl bg-[#FFBB98]/10 border border-white/40/20 mb-8">
                            <BodyText className="text-[#2A8585] leading-relaxed text-center italic">
                                Even in hurt or fear, it becomes possible to turn toward each other and feel met.
                            </BodyText>
                        </div>

                        {/* Summary of what was shared */}
                        {(selectedSofter || selectedNeed) && (
                            <div className="glass-card rounded-2xl p-5">
                                <SmallText className="text-[#7D8E95] font-medium mb-3 block text-center">
                                    What you noticed today:
                                </SmallText>
                                <div className="space-y-2">
                                    {selectedSofter && (
                                        <SmallText className="text-[#2A8585] block text-center">
                                            Softer feeling: <span className="italic">{selectedSofter.label}</span>
                                        </SmallText>
                                    )}
                                    {selectedNeed && (
                                        <SmallText className="text-[#2A8585] block text-center">
                                            What you needed: <span className="italic">{selectedNeed.label}</span>
                                        </SmallText>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="space-y-4">
                            <PrimaryButton
                                onClick={handleClose}
                                className="w-full"
                                size="xl"
                                data-testid="done-btn"
                            >
                                <Heart className="w-4 h-4 mr-2" />
                                Done
                            </PrimaryButton>
                            <SecondaryButton
                                onClick={handleStartOver}
                                className="w-full"
                                size="lg"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Start over
                            </SecondaryButton>
                        </div>

                        <div className="mt-8 text-center">
                            <SmallText className="text-[#7D8E95]">
                                You can return to this tool anytime you need to reconnect.
                            </SmallText>
                        </div>

                        {/* Entry to Relationship Cycles module */}
                        <div className="mt-6 pt-6 border-t border-white/40/60">
                            <Link to="/regulate/relationship-cycles" className="block">
                                <div className="p-4 rounded-2xl bg-[#7D8E95]/10 hover:bg-[#7D8E95]/20 transition-colors border border-[#7D8E95]/20 group">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-[#7D8E95]/30 flex items-center justify-center flex-shrink-0">
                                            <RefreshCw className="w-4 h-4 text-[#7D8E95]" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <SmallText className="text-[#2A8585] font-medium block mb-0.5">
                                                Want to understand your cycle better?
                                            </SmallText>
                                            <SmallText className="text-[#7D8E95] group-hover:text-[#7D8E95] transition-colors">
                                                Explore relationship patterns (optional)
                                            </SmallText>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-[#7D8E95] group-hover:text-[#7D8E95] transition-colors" />
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default WhatDoWeNeed;
